/* Prosperr Offline utilities — IndexedDB-based mutation queue + GET response cache.
 *
 * Responsibilities:
 *   - Queue POST/PUT/PATCH/DELETE requests when the browser is offline.
 *   - Replay queued mutations (FIFO) once connectivity is back.
 *   - Cache safe GET responses so the UI keeps working without network.
 */

const DB_NAME = "prosperr-offline";
const DB_VERSION = 1;
const STORE_QUEUE = "queue";
const STORE_CACHE = "get_cache";

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: "url" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

async function tx(store, mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const out = fn(s);
    t.oncomplete = () => resolve(out?.result ?? out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

/* ----------------- Mutation Queue ----------------- */

export async function enqueueMutation(entry) {
  // entry: { method, url, data, headers, createdAt }
  try {
    await tx(STORE_QUEUE, "readwrite", (s) => s.add({ ...entry, createdAt: Date.now() }));
    notifyQueueChanged();
    return true;
  } catch {
    return false;
  }
}

export async function getQueue() {
  try {
    return await tx(STORE_QUEUE, "readonly", (s) => {
      return new Promise((resolve) => {
        const items = [];
        const cursorReq = s.openCursor();
        cursorReq.onsuccess = () => {
          const c = cursorReq.result;
          if (c) { items.push(c.value); c.continue(); } else resolve(items);
        };
        cursorReq.onerror = () => resolve([]);
      });
    });
  } catch {
    return [];
  }
}

export async function getQueueCount() {
  try {
    return await tx(STORE_QUEUE, "readonly", (s) => {
      return new Promise((resolve) => {
        const req = s.count();
        req.onsuccess = () => resolve(req.result || 0);
        req.onerror = () => resolve(0);
      });
    });
  } catch { return 0; }
}

export async function removeFromQueue(id) {
  try {
    await tx(STORE_QUEUE, "readwrite", (s) => s.delete(id));
    notifyQueueChanged();
  } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
}

export async function clearQueue() {
  try {
    await tx(STORE_QUEUE, "readwrite", (s) => s.clear());
    notifyQueueChanged();
  } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
}

/* ----------------- GET cache ----------------- */

export async function cacheGet(url, data) {
  try {
    await tx(STORE_CACHE, "readwrite", (s) => s.put({ url, data, ts: Date.now() }));
  } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
}

export async function readGetCache(url) {
  try {
    return await tx(STORE_CACHE, "readonly", (s) => {
      return new Promise((resolve) => {
        const r = s.get(url);
        r.onsuccess = () => resolve(r.result?.data ?? null);
        r.onerror = () => resolve(null);
      });
    });
  } catch { return null; }
}

/** Count cached prospects (sum array length from any /prospects GET cache entry). */
export async function countCachedProspects() {
  try {
    return await tx(STORE_CACHE, "readonly", (s) => {
      return new Promise((resolve) => {
        let max = 0;
        const cursorReq = s.openCursor();
        cursorReq.onsuccess = () => {
          const c = cursorReq.result;
          if (c) {
            const v = c.value;
            if (v?.url && /\/api\/prospects(\?|$)/.test(v.url) && Array.isArray(v.data)) {
              if (v.data.length > max) max = v.data.length;
            }
            c.continue();
          } else {
            resolve(max);
          }
        };
        cursorReq.onerror = () => resolve(0);
      });
    });
  } catch { return 0; }
}

/* ----------------- Flush (replay) ----------------- */

export async function flushQueue(apiClient, onProgress) {
  if (!navigator.onLine) return { flushed: 0, failed: 0 };
  const items = await getQueue();
  if (!items.length) return { flushed: 0, failed: 0 };

  let flushed = 0; let failed = 0;
  // FIFO order
  items.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

  for (const item of items) {
    try {
      await apiClient.request({
        method: item.method,
        url: item.url,
        data: item.data,
        headers: { ...(item.headers || {}), "X-Offline-Replay": "1" },
      });
      await removeFromQueue(item.id);
      flushed++;
      onProgress?.({ flushed, failed, remaining: items.length - flushed - failed });
    } catch (err) {
      // Network error → stop (will retry on next online)
      const status = err?.response?.status;
      if (!status) break;
      // 4xx (except 408/429) → drop (client error, won't succeed on retry)
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        await removeFromQueue(item.id);
        failed++;
        onProgress?.({ flushed, failed, remaining: items.length - flushed - failed });
      } else {
        // 5xx/timeout → stop and retry later
        break;
      }
    }
  }
  return { flushed, failed };
}

/* ----------------- Event bus ----------------- */

const listeners = new Set();

export function subscribeQueue(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notifyQueueChanged() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
  }
}

/* ----------------- Helpers ----------------- */

export function isMutation(method) {
  const m = String(method || "").toLowerCase();
  return m === "post" || m === "put" || m === "patch" || m === "delete";
}

// Endpoints that must never be queued offline (auth, AI, exports, payments…)
const NO_QUEUE_PATTERNS = [
  /^\/auth\//,
  /^\/ai\//,
  /^\/export/,
  /^\/import/,
  /^\/stripe\//,
  /^\/seed/,
  /^\/webhook\//,
];

export function canQueueUrl(url) {
  const path = String(url || "").replace(/^https?:\/\/[^/]+/i, "").replace(/^\/api/, "");
  return !NO_QUEUE_PATTERNS.some((re) => re.test(path));
}

/** Return a Set of ids currently in the mutation queue.
 * Parses URLs like /collection/:id to collect pending IDs for optimistic UI indicators. */
export async function getPendingIds() {
  const out = new Set();
  try {
    const items = await getQueue();
    for (const it of items) {
      const clean = String(it.url || "").replace(/^https?:\/\/[^/]+/i, "").split("?")[0];
      const parts = clean.replace(/^\/api\//, "").split("/").filter(Boolean);
      if (parts.length >= 2 && parts[1]) out.add(parts[1]);
    }
  } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
  return out;
}

/** Patch a cached GET response to reflect an optimistic mutation.
 * Handles list responses (e.g. /prospects) and single-item responses (/prospects/:id).
 * When offline, this lets the UI show the user's change without waiting for sync.
 */
export async function optimisticallyPatchCache(url, method, data) {
  try {
    // Extract path without query string & base url. Normalize to always start with /api/.
    let clean = String(url || "").replace(/^https?:\/\/[^/]+/i, "").split("?")[0];
    if (!clean.startsWith("/api/")) clean = "/api" + (clean.startsWith("/") ? "" : "/") + clean;
    const parts = clean.replace(/^\/api\//, "").split("/").filter(Boolean);
    // e.g. ["prospects", "pros_xxx"] or ["actions", "act_xxx"] or ["users", "me"]
    if (parts.length < 2) return;
    const collection = parts[0];
    const itemId = parts[1];

    // 1) Patch cached single-item GET if present
    await tx(STORE_CACHE, "readwrite", (s) => {
      return new Promise((resolve) => {
        const cursorReq = s.openCursor();
        cursorReq.onsuccess = () => {
          const c = cursorReq.result;
          if (!c) return resolve();
          const entry = c.value;
          const entryClean = String(entry.url || "").replace(/^https?:\/\/[^/]+/i, "").split("?")[0].split("#")[0];
          // Single item: /api/collection/id
          const singleRe = new RegExp(`^/api/${collection}/${escapeRegExp(itemId)}$`);
          if (singleRe.test(entryClean)) {
            if (method === "delete") {
              c.delete();
            } else if (entry.data && typeof entry.data === "object") {
              c.update({ ...entry, data: { ...entry.data, ...(data || {}), _pending_sync: true }, ts: Date.now() });
            }
          }
          // List: /api/collection   → patch matching item within the array
          const listRe = new RegExp(`^/api/${collection}/?$`);
          if (listRe.test(entryClean) && Array.isArray(entry.data)) {
            const idFieldCandidates = [collection.replace(/s$/, "") + "_id", "id"];
            const idField = idFieldCandidates.find((f) => entry.data.some((x) => x && x[f] !== undefined));
            if (idField) {
              if (method === "delete") {
                const newList = entry.data.filter((x) => x[idField] !== itemId);
                c.update({ ...entry, data: newList, ts: Date.now() });
              } else {
                const newList = entry.data.map((x) =>
                  x[idField] === itemId ? { ...x, ...(data || {}), _pending_sync: true } : x
                );
                c.update({ ...entry, data: newList, ts: Date.now() });
              }
            }
          }
          c.continue();
        };
        cursorReq.onerror = () => resolve();
      });
    });
    notifyQueueChanged();
  } catch (e) { if (typeof console !== 'undefined') console.debug('[offline cache]', e?.message || e); }
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
